/* eslint-disable unicorn/no-array-reduce */
import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'
import { QuickAdd } from '@components/QuickAdd/QuickAdd'
import { useHeader } from '@velasca/hooks/use-header'
import { useViewportBottomLeft } from '@velasca/hooks/use-viewport-bottom-left'
import type { Image, StaffPickIcon } from '@velasca/types'
import { Button } from '@velasca/ui/button'
import { ErrorMessage } from '@velasca/ui/error-message'
import { HorizontalCardPrice } from '@velasca/ui/horizontal-card-price'
import { PriceRow } from '@velasca/ui/price-row'
import { SansText } from '@velasca/ui/sans-text'
import { SerifTitle } from '@velasca/ui/serif-title'
import { getFeaturedImage } from '@velasca/utils/get-featured-image'
import { handleError } from '@velasca/utils/handle-error'
import { scrollTo } from '@velasca/utils/scroll-to'
import { useExchangeRates } from '../ExchangeRatesProvider'
import { useStaffPickImage } from '../StaffPickImageProvider'
import {
  Background,
  ButtonWrapper,
  DesktopErrorMessageWrapper,
  DesktopSelectedSize,
  Details,
  MobileErrorMessageWrapper,
  MobileSelectedSize,
  ProductInfo,
  Row,
} from './ProductStickyBar.styles'
import type { ProductStickyBarProps } from './ProductStickyBar.types'

export const ProductStickyBar = ({
  product,
  currentSize,
  buttonMessage,
  error,
  isSizeSelected,
  isSizeAvailable,
  hasOneSize,
  scrollToCallback,
}: ProductStickyBarProps): JSX.Element => {
  const {
    rawPrice,
    rawCompareAtPrice,
    title,
    customProductType,
    productType,
    frontImageIndex,
    sizes,
    description,
    handle,
    isGiftCard,
    medias,
    isNew,
    staffPickIconID,
  } = product
  const { convert } = useExchangeRates()
  const offsetHeight = useHeader(({ headerHeight }) => headerHeight)
  const [isQuickAddPopupOpen, setIsQuickAddPopupOpen] = useState<boolean>(false)
  const { t } = useTranslation('product')
  const stickyBarRef = useRef<HTMLDivElement>(null)
  const isMinWidth768px = useMediaQuery('(min-width: 768px)')

  useViewportBottomLeft()

  const formattedPrice = useMemo(() => {
    if (!rawPrice) {
      return ''
    }

    return convert(rawPrice)
  }, [rawPrice, convert])

  const formattedCompareAtPrice = useMemo(() => {
    if (!rawCompareAtPrice) {
      return ''
    }

    return convert(rawCompareAtPrice)
  }, [rawCompareAtPrice, convert])

  const isPriceDiscounted = useMemo(() => {
    return Boolean(
      rawCompareAtPrice && rawPrice && rawCompareAtPrice > rawPrice,
    )
  }, [rawCompareAtPrice, rawPrice])

  // SHOP-3508: Refactoring useStaffPickIcon
  const { staffPickIcons, settings } = useStaffPickImage()
  const getStaffPickIcon = useCallback(() => {
    // if (staffPickIcons && !loading) {
    if (staffPickIcons) {
      const currentStaffPickIcon = staffPickIcons.find(
        (staffPickIcon: StaffPickIcon) => staffPickIcon.id === staffPickIconID,
      )

      if (currentStaffPickIcon) {
        return currentStaffPickIcon
      }

      // If product is not new, there is no point in looking for a new icon
      if (!isNew) {
        return
      }

      if (!settings?.newID) {
        handleError(
          new Error(
            'Product is new but there is no newID defined. Define a newID in the CMS',
          ),
        )

        return
      }

      const newIcon = staffPickIcons.find(
        (staffPickIcon: StaffPickIcon) => staffPickIcon?.id === settings.newID,
      )

      if (!newIcon) {
        handleError(
          new Error('No new staff pick icon found, plese add it to cms'),
        )
      }

      return newIcon
    }

    return undefined
  }, [staffPickIcons, isNew, settings?.newID, staffPickIconID])

  // TODO: SHOP-3009 - Uniformare le immagini dei prodotti
  const images = useMemo(() => {
    if (!medias) {
      return []
    }

    return medias.reduce((acc: Image[], item: any) => {
      if (item.type === 'image') {
        acc.push({
          src: item.src,
          alt: item.alt,
        })
      }

      return acc
    }, [])
  }, [medias])

  const featuredImage = useMemo(
    () =>
      getFeaturedImage(
        images,
        isGiftCard ?? false,
        handle,
        customProductType,
        frontImageIndex,
      ),
    [images, isGiftCard, handle, customProductType, frontImageIndex],
  )

  const isValidSizeSelected = isSizeSelected || hasOneSize
  const isSelectedSizeAvailable = isValidSizeSelected && isSizeAvailable

  const handleAddToCart = useCallback(() => {
    if (isValidSizeSelected) {
      if (isSelectedSizeAvailable) {
        // With a valid and available size, rely on the native form submit to add to cart
        return undefined
      }

      // If a valid size is selected but it's unavailable, scroll to the notify me form
      scrollTo('#size-picker', offsetHeight)
      scrollToCallback()
    } else {
      if (isMinWidth768px) {
        // If no size is selected and the screen width is greater than or equal to 768px, scroll to the sizes section
        scrollTo('#size-picker', offsetHeight)
        scrollToCallback()
      } else {
        // If no size is selected and the screen width is less than 768px, open the quick add popup
        setIsQuickAddPopupOpen(true)
      }
    }
  }, [
    isMinWidth768px,
    isSelectedSizeAvailable,
    isValidSizeSelected,
    offsetHeight,
    scrollToCallback,
  ])

  return (
    <>
      <Background
        ref={stickyBarRef}
        initial={{ opacity: 0, y: 700 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        exit={{ opacity: 0, y: 700 }}
      >
        <AnimatePresence>
          {currentSize && (
            <MobileSelectedSize
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <HorizontalCardPrice>
                {hasOneSize
                  ? currentSize.name
                  : `${t('selectedSize')} ${currentSize.name}`}
              </HorizontalCardPrice>
              <PriceRow>
                <SansText
                  fontWeight="500"
                  dangerouslySetInnerHTML={{
                    __html: formattedPrice,
                  }}
                />

                {isPriceDiscounted && (
                  <HorizontalCardPrice
                    strikethrough
                    color="gray"
                    dangerouslySetInnerHTML={{
                      __html: formattedCompareAtPrice,
                    }}
                  />
                )}
              </PriceRow>
            </MobileSelectedSize>
          )}
        </AnimatePresence>
        <Row>
          <ProductInfo>
            <div>
              <SerifTitle as="h1" color="primary">
                {title}
              </SerifTitle>
              <Details>
                <SansText>{productType}</SansText>
                {description?.color && <SansText>{description.color}</SansText>}
              </Details>
            </div>

            <AnimatePresence>
              {currentSize && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <DesktopSelectedSize>
                    <PriceRow>
                      <HorizontalCardPrice
                        dangerouslySetInnerHTML={{
                          __html: formattedPrice,
                        }}
                      />
                      {isPriceDiscounted && (
                        <HorizontalCardPrice
                          strikethrough
                          color="gray"
                          dangerouslySetInnerHTML={{
                            __html: formattedCompareAtPrice,
                          }}
                        />
                      )}
                    </PriceRow>
                    <SansText>
                      {hasOneSize
                        ? currentSize.name
                        : `${t('selectedSize')} ${currentSize.name}`}
                    </SansText>
                  </DesktopSelectedSize>
                </motion.div>
              )}
            </AnimatePresence>
          </ProductInfo>
          <ButtonWrapper>
            <Button
              fullWidth
              text={buttonMessage}
              type={isSelectedSizeAvailable ? 'submit' : 'button'}
              form={isSelectedSizeAvailable ? 'addToCartForm' : undefined}
              onClick={handleAddToCart}
            />
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <DesktopErrorMessageWrapper>
                    <ErrorMessage>{error}</ErrorMessage>
                  </DesktopErrorMessageWrapper>
                </motion.div>
              )}
            </AnimatePresence>
          </ButtonWrapper>
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <MobileErrorMessageWrapper>
                  <ErrorMessage>{error}</ErrorMessage>
                </MobileErrorMessageWrapper>
              </motion.div>
            )}
          </AnimatePresence>
        </Row>
      </Background>
      <QuickAdd
        shouldOpenCartDrawerOnAddToCart
        staffPickIcon={getStaffPickIcon()}
        title={title || ''}
        price={formattedPrice}
        productType={productType}
        isGiftCard={isGiftCard}
        sizes={sizes}
        handle={handle}
        isOpen={isQuickAddPopupOpen}
        image={featuredImage || { src: '' }}
        compareAtPrice={formattedCompareAtPrice}
        isPriceDiscounted={isPriceDiscounted}
        description={{
          thin: description?.color ?? '',
          bold: description?.composition ?? '',
        }}
        onClose={() => {
          setIsQuickAddPopupOpen(false)
        }}
      />
    </>
  )
}
